<template>
  <section id="demo" class="demo-dashboard landing-section">
    <div class="landing-container">
      <header class="demo-dashboard__head">
        <span class="demo-badge">DEMO</span>
        <h2 class="demo-dashboard__title">Протестируйте систему на готовых сценариях</h2>
        <p class="demo-dashboard__lead">
          Выберите отрасль и откройте тендер как в рабочей области: таблица результатов и карточка с анализом,
          критериями, товарами, рисками и чатом с ИИ
        </p>
      </header>

      <div class="demo-dashboard__scenarios-wrap">
        <div class="demo-dashboard__scenarios" role="tablist" aria-label="Отрасли">
          <button
            v-for="scenario in scenarios"
            :key="scenario.id"
            type="button"
            role="tab"
            class="scenario-chip"
            :class="{ 'scenario-chip--active': scenario.id === activeId }"
            :aria-selected="scenario.id === activeId"
            @click="selectScenario(scenario.id)"
          >
            <span class="scenario-chip__icon" aria-hidden="true">{{ scenarioIcon(scenario.icon) }}</span>
            <span class="scenario-chip__label">{{ scenario.label }}</span>
          </button>
        </div>
      </div>

      <div v-if="activeScenario" class="demo-dashboard__workspace">
        <LandingDemoTenderTable
          :rows="demoRows"
          :locked-count="activeScenario.lockedCount"
          @open-details="openDemoDetails"
          @open-quiz="$emit('open-quiz')"
        />

        <button type="button" class="btn btn--secondary demo-dashboard__all-btn" @click="$emit('open-quiz')">
          Смотреть все результаты в системе
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>

    <LandingDemoTenderModal
      :show="demoModalOpen"
      :payload="selectedDemoPayload"
      @close="closeDemoModal"
      @open-quiz="$emit('open-quiz')"
    />
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'

import LandingDemoTenderTable from '@/components/landing/LandingDemoTenderTable.vue'
import LandingDemoTenderModal from '@/components/landing/LandingDemoTenderModal.vue'
import { LANDING_DEMO_SCENARIOS } from '@/landing/demoScenarios'
import { mapDemoTenderToModalPayload, mapScenarioToRows } from '@/landing/demoTenderRows'

defineEmits(['open-quiz'])

const scenarios = LANDING_DEMO_SCENARIOS
const activeId = ref('cleaning')
const demoModalOpen = ref(false)
const selectedDemoPayload = ref(null)

const activeScenario = computed(() => scenarios.find((s) => s.id === activeId.value) || scenarios[0])

const demoRows = computed(() => {
  const scenario = activeScenario.value
  return scenario ? mapScenarioToRows(scenario) : []
})

function selectScenario(id) {
  activeId.value = id
  closeDemoModal()
}

function openDemoDetails(row) {
  selectedDemoPayload.value = mapDemoTenderToModalPayload(row)
  demoModalOpen.value = true
}

function closeDemoModal() {
  demoModalOpen.value = false
  selectedDemoPayload.value = null
}

const ICONS = {
  calendar: '◷',
  pump: '◎',
  server: '▣',
  clean: '◉',
  build: '⬡',
  medical: '✚',
}

function scenarioIcon(name) {
  return ICONS[name] || '◆'
}
</script>

<style scoped>
.demo-dashboard {
  padding: clamp(2.8rem, 6vw, 4.2rem) 0;
  background: rgba(255, 255, 255, 0.45);
}

.demo-dashboard__head {
  text-align: center;
  margin: 0 auto 1.75rem;
  max-width: 44rem;
  padding: 0 0.25rem;
}

.demo-badge {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 0.2rem 0.55rem;
  border-radius: 6px;
  background: #dbeafe;
  color: var(--blue-accent);
  margin-bottom: 0.5rem;
}

.demo-dashboard__title {
  margin: 0 0 0.5rem;
  font-size: clamp(1.35rem, 3vw, 1.85rem);
  font-weight: 700;
  color: var(--fg);
}

.demo-dashboard__lead {
  margin: 0 auto;
  max-width: 44rem;
  color: var(--muted);
  line-height: 1.6;
}

.demo-dashboard__scenarios-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto 1.5rem;
  padding: 0 clamp(0.25rem, 2vw, 0.5rem);
  box-sizing: border-box;
  overflow-x: auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.demo-dashboard__scenarios {
  display: grid;
  grid-template-columns: repeat(6, minmax(7.25rem, 1fr));
  gap: 0.75rem;
  width: 100%;
  min-width: min(100%, 44rem);
  margin: 0 auto;
  justify-items: stretch;
  align-items: stretch;
}

.scenario-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  min-height: 5.25rem;
  padding: 0.75rem 0.5rem;
  border: 1px solid var(--border-strong, #cbd5e1);
  border-radius: 14px;
  background: var(--surface, #fff);
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.scenario-chip:hover {
  border-color: #92a8c8;
  box-shadow: 0 4px 14px rgba(0, 113, 227, 0.1);
}

.scenario-chip--active {
  border-color: var(--blue-accent, #0071e3);
  background: #eaf1ff;
  box-shadow: 0 4px 16px rgba(0, 113, 227, 0.14);
}

.scenario-chip:focus-visible {
  outline: 2px solid var(--blue-accent, #0071e3);
  outline-offset: 2px;
}

.scenario-chip__icon {
  flex-shrink: 0;
  font-size: 1.35rem;
  color: var(--blue-accent, #0071e3);
  line-height: 1;
}

.scenario-chip__label {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 500;
  text-align: center;
  color: var(--muted, #515154);
  line-height: 1.3;
  max-width: 100%;
  overflow-wrap: break-word;
  hyphens: auto;
}

.demo-dashboard__workspace {
  background: #edf2f9;
  border: 1px solid var(--border-strong, #cbd5e1);
  border-radius: 18px;
  padding: clamp(1rem, 2.5vw, 1.35rem);
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  box-sizing: border-box;
}

@media (min-width: 1024px) {
  .demo-dashboard__scenarios {
    min-width: 100%;
  }

  .demo-dashboard__scenarios-wrap {
    overflow-x: visible;
  }
}

.demo-dashboard__all-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  margin-top: 1.15rem;
  padding: 0.7rem 1.25rem;
  border-radius: 999px;
  border: 1px solid var(--border-strong, #cbd5e1);
  background: #fff;
  color: var(--fg, #1d1d1f);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  font-family: inherit;
  box-sizing: border-box;
}

.demo-dashboard__all-btn:hover {
  border-color: #92a8c8;
  background: #f8fafc;
}
</style>
